import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import {
  WALLET_TYPES,
  getWalletType,
  sanitizeWalletAddress,
  validateWalletDestination,
} from '../../constants/walletTypes';

const WalletEdit = () => {
  const { logout } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const [selectedType, setSelectedType] = useState('');
  const [label, setLabel] = useState('');
  const [destination, setDestination] = useState('');
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!uuid) return;

    const fetchWallet = async () => {
      try {
        setLoadingWallet(true);
        setError('');
        const response = await api.get(`/wallets/key/${uuid}`);
        const data = response.data || {};
        const type = getWalletType(data.type || data.walletType);
        setSelectedType(type?.id || '');
        setLabel(data.label || '');
        setDestination(data.address || '');
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            err.message ||
            'Nao foi possivel carregar a wallet.'
        );
      } finally {
        setLoadingWallet(false);
      }
    };

    fetchWallet();
  }, [uuid]);

  const walletType = useMemo(
    () => WALLET_TYPES.find((type) => type.id === selectedType),
    [selectedType]
  );

  const destinationError = useMemo(() => {
    if (!walletType || !destination) return '';
    return validateWalletDestination(walletType.id, destination);
  }, [destination, walletType]);

  const canSubmit = Boolean(walletType && destination.trim() && !destinationError && !loading);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleSelectType = (typeId) => {
    setSelectedType(typeId);
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!walletType) {
      setError('Escolha o tipo de wallet.');
      return;
    }

    const validationMessage = validateWalletDestination(walletType.id, destination);
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    try {
      setLoading(true);
      setError('');

      await api.put(`/wallets/${uuid}`, {
        type: walletType.apiValue,
        label: label.trim() || walletType.label,
        address: destination.trim(),
      });

      showToast('Wallet atualizada com sucesso.');
      navigate('/wallets');
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Nao foi possivel atualizar a wallet.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="wallet" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => navigate('/wallets')}
              className="text-sm font-semibold flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="bi-arrow-left-short text-xl"></i> Voltar
            </button>

            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="mx-auto max-w-4xl">
            <div className="mb-6">
              <h1 className="text-2xl font-semibold text-slate-900">Editar Wallet</h1>
              <p className="text-sm text-slate-500">Atualize o tipo, o nome ou o endereco da wallet salva.</p>
            </div>

            {loadingWallet ? (
              <div className="text-sm text-gray-500">Carregando wallet...</div>
            ) : (
              <>
                <div className="grid gap-4 md:grid-cols-3">
                  {WALLET_TYPES.map((type) => {
                    const isSelected = selectedType === type.id;

                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => handleSelectType(type.id)}
                        className={`rounded-3xl border bg-white px-5 py-6 text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-md ${
                          isSelected ? 'border-tBlue ring-2 ring-tBlue/20' : 'border-slate-200'
                        }`}
                      >
                        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-xl text-slate-700">
                          <i className={type.icon}></i>
                        </div>
                        <h2 className="text-base font-semibold text-slate-900">{type.label}</h2>
                        <p className="mt-2 text-xs text-slate-500">{type.description}</p>
                        <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-tBlue">
                          {isSelected ? 'Selecionada' : 'Escolher'} <i className="bi-arrow-right-short text-lg"></i>
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-6 rounded-3xl border border-slate-200 bg-white px-6 py-8 shadow-sm">
                  <h2 className="text-lg font-semibold text-slate-900">
                    {walletType ? `Dados da wallet ${walletType.shortLabel}` : 'Selecione um tipo para continuar'}
                  </h2>

                  {error && (
                    <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
                  )}

                  <form className="mt-6 flex flex-col gap-5" onSubmit={handleSubmit}>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Nome da wallet
                      </label>
                      <input
                        type="text"
                        value={label}
                        onChange={(event) => setLabel(event.target.value.slice(0, 60))}
                        placeholder={walletType ? `Ex: ${walletType.shortLabel} principal` : 'Escolha um tipo primeiro'}
                        disabled={!walletType}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-slate-400 focus:border-slate-400 disabled:bg-slate-100"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Wallet
                      </label>
                      <textarea
                        value={destination}
                        onChange={(event) => {
                          setDestination(sanitizeWalletAddress(event.target.value));
                          setError('');
                        }}
                        placeholder={walletType?.placeholder || 'Escolha um tipo primeiro'}
                        disabled={!walletType}
                        rows={3}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 font-mono text-sm outline-none transition-colors placeholder:text-slate-400 focus:border-slate-400 disabled:bg-slate-100"
                      />
                      {destinationError ? (
                        <p className="mt-2 text-xs font-semibold text-red-600">{destinationError}</p>
                      ) : null}
                    </div>

                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className={`inline-flex items-center justify-center rounded-full px-6 py-2 text-sm font-semibold transition-colors ${
                        canSubmit ? 'bg-tBlue text-white hover:bg-tBlue2' : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {loading ? 'Salvando...' : 'Salvar alteracoes'}
                    </button>
                  </form>
                </div>
              </>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default WalletEdit;
